import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade1EngineeringLesson.css";

// 3D dependencies:
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { Physics, useBox, usePlane, useSphere } from "@react-three/cannon";

/**
 * 1) A “plane” that acts as our ground/base
 *    so the ball doesn’t fall forever in the physics world.
 */
function Ground(props) {
  // A static plane at y = -2
  usePlane(() => ({ rotation: [-Math.PI / 2, 0, 0], position: [0, -2, 0], ...props }));
  return null;
}

/**
 * InteractiveSeesaw:
 * A lever the user can tilt with a slider, plus a rolling ball.
 * This replaces the old static “Lever3D”.
 */
function InteractiveSeesaw() {
  const [tilt, setTilt] = useState(0); // tilt angle in radians

  // The seesaw uses a box body with mass=0 => static
  // The ball is a sphere with mass>0 => it rolls under gravity
  function Seesaw() {
    // useBox for the plank
    const [ref] = useBox(() => ({
      type: "Static",
      position: [0, 0, 0],
      rotation: [0, 0, tilt], // tilt around z-axis
      args: [6, 0.2, 1], // length=6, thickness=0.2, width=1
    }));
    return (
      <mesh ref={ref}>
        <boxGeometry args={[6, 0.2, 1]} />
        <meshStandardMaterial color="orange" />
      </mesh>
    );
  }

  function RollingBall() {
    // The ball has mass=1 => it can roll
    const [ref] = useSphere(() => ({
      mass: 1,
      position: [-2, 1, 0], // Start near the left side
      args: [0.3], // radius=0.3
    }));
    return (
      <mesh ref={ref}>
        <sphereGeometry args={[0.3, 16, 16]} />
        <meshStandardMaterial color="blue" />
      </mesh>
    );
  }

  return (
    <div style={{ width: "400px", margin: "auto", textAlign: "center" }}>
      <h4>Interactive Lever: Tilt the board!</h4>
      <p>Drag the slider to tilt. The ball will roll to the lower side.</p>
      <input
        type="range"
        min={-0.7} // ~ -40 degrees
        max={0.7}  // ~ +40 degrees
        step={0.01}
        value={tilt}
        onChange={(e) => setTilt(parseFloat(e.target.value))}
        style={{ width: "200px", margin: "10px 0" }}
      />
      <p>Tilt: {tilt.toFixed(2)} radians</p>

      <div style={{ width: "400px", height: "300px", background: "#f0f0f0", margin: "auto" }}>
        <Canvas>
          <OrbitControls enablePan={false} />
          <ambientLight intensity={0.5} />
          <directionalLight position={[10, 10, 10]} intensity={1} />

          <Physics gravity={[0, -9.81, 0]}>
            {/* ground plane at y=-2 */}
            <Ground />
            {/* The seesaw plank */}
            <Seesaw />
            {/* The rolling ball */}
            <RollingBall />
          </Physics>
        </Canvas>
      </div>
    </div>
  );
}
/* =========================
   2) InteractiveInclinedPlane
   =========================
   - A ramp with angle slider + rolling ball
   - The user can adjust the ramp's slope.
*/
function InteractiveInclinedPlane() {
    const [angle, setAngle] = useState(0.3); // default ~ 17 degrees
  
    // The ramp as a static box
    function Ramp() {
      const [ref] = useBox(() => ({
        type: "Static",
        position: [0, 0, 0],
        rotation: [0, 0, -angle], // negative = tilt to the left
        args: [6, 0.2, 2], // length, thickness, width
      }));
      return (
        <mesh ref={ref}>
          <boxGeometry args={[6, 0.2, 2]} />
          <meshStandardMaterial color="#3498db" />
        </mesh>
      );
    }
  
    // The ball that slides on the ramp
    function SlidingBall() {
      const [ref] = useSphere(() => ({
        mass: 1,
        position: [-2, 2, 0], // start near left side
        args: [0.3],
        friction: 0.2,
      }));
      return (
        <mesh ref={ref}>
          <sphereGeometry args={[0.3, 16, 16]} />
          <meshStandardMaterial color="red" />
        </mesh>
      );
    }
  
    return (
      <div style={{ width: "400px", margin: "auto", textAlign: "center" }}>
        <h4>Interactive Inclined Plane</h4>
        <p>Use the slider to change the ramp’s angle. The ball slides down!</p>
        <input
          type="range"
          min={0}
          max={0.7} // about 40 degrees
          step={0.01}
          value={angle}
          onChange={(e) => setAngle(parseFloat(e.target.value))}
          style={{ width: "200px", margin: "10px 0" }}
        />
        <p>Angle: {(angle * (180 / Math.PI)).toFixed(1)}°</p>
  
        <div style={{ width: "400px", height: "300px", background: "#f0f0f0", margin: "auto" }}>
          <Canvas>
            <OrbitControls enablePan={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[10, 10, 10]} intensity={1} />
            <Physics gravity={[0, -9.81, 0]}>
              <Ground />
              <Ramp />
              <SlidingBall />
            </Physics>
          </Canvas>
        </div>
      </div>
    );
  }
  
  /* =========================
     3) InteractiveWheelAxle
     =========================
     - A wheel & axle the user can spin with a slider
     - This is a simpler approach: we just rotate the wheel in real-time,
       not physically simulating it with collisions, etc.
  */
       function InteractiveWheelAxle() {
        const [spin, setSpin] = useState(0);
        
        // We'll spin only the wheel group (not the axle).
        const wheelRef = useRef();
      
        // Animate the wheel around the X-axis each frame
        useEffect(() => {
          let frameId;
          const animate = () => {
            if (wheelRef.current) {
              // Rotate around X
              wheelRef.current.rotation.x += spin * 0.01;
            }
            frameId = requestAnimationFrame(animate);
          };
          animate();
          return () => cancelAnimationFrame(frameId);
        }, [spin]);
      
        return (
          <div style={{ width: 400, margin: "auto", textAlign: "center" }}>
            <h4>Interactive Wheel &amp; Axle</h4>
            <p>
              A stationary axle with a spinning wheel (with small colored patches on
              its surface for visibility).
            </p>
      
            <input
              type="range"
              min={-5}
              max={5}
              step={0.1}
              value={spin}
              onChange={(e) => setSpin(parseFloat(e.target.value))}
              style={{ width: 200, margin: "10px 0" }}
            />
            <p>Spin Speed: {spin.toFixed(1)}</p>
      
            <div style={{ width: 400, height: 300, background: "#f0f0f0", margin: "auto" }}>
              <Canvas camera={{ position: [6, 6, 6], fov: 45 }}>
                <OrbitControls enablePan={false} />
                <ambientLight intensity={0.5} />
                <directionalLight position={[10, 10, 10]} intensity={1} />
      
                {/* Base platform at y=-2.5 */}
                <mesh position={[0, -2.5, 0]}>
                  <boxGeometry args={[8, 0.5, 4]} />
                  <meshStandardMaterial color="gray" />
                </mesh>
      
                {/**
                 * Group at y=-1.5 for the axle/wheel. 
                 * The axle is stationary, the wheel spins in a separate group ref.
                 */}
                <group position={[0, -1.5, 0]}>
                  {/* Stationary Axle: horizontally oriented by rotating 90° around Z */}
                  <mesh rotation={[0, 0, Math.PI / 2]}>
                    <cylinderGeometry args={[0.1, 0.1, 2.0, 32]} />
                    <meshStandardMaterial color="#8B4513" />
                  </mesh>
      
                  {/* The spinning wheel group */}
                  <group ref={wheelRef}>
                    {/* 1) Main torus for the tire, oriented vertically (rotate 90° around Y) */}
                    <mesh rotation={[0, Math.PI / 2, 0]}>
                      <torusGeometry args={[0.8, 0.15, 16, 32]} />
                      <meshStandardMaterial color="orange" />
                    </mesh>
      
                    {/* 2) Color patches on the torus surface */}
                    <ColorPatchesOnWheel />
                  </group>
                </group>
              </Canvas>
            </div>
          </div>
        );
      }
      
      /**
       * ColorPatchesOnWheel:
       * We place smaller squares on the "outer surface" of the torus (near radius=0.95),
       * distributing them evenly in a circular pattern around the wheel's circumference.
       * 
       * Because the wheel is oriented so that rotating around X spins it, 
       * we revolve these patches around X in local coords, placing each patch at y=some, z=some.
       */
      function ColorPatchesOnWheel() {
        const patchCount = 8;
        // Outer radius of the torus is ~ 0.8 + 0.15 = 0.95.
        // We'll place patches near 0.9, so they look like stripes on the wheel's surface.
        const radius = 0.9;
        const colors = [
          "#ff0000", "#00ff00", "#0000ff", "#ffff00",
          "#ff00ff", "#00ffff", "#ffaa00", "#ffffff"
        ];
      
        const patches = [];
        for (let i = 0; i < patchCount; i++) {
          const angle = (i * 2 * Math.PI) / patchCount;
          patches.push(
            <group key={i} rotation={[angle, 0, 0]}>
              {/**
               * Each patch is placed at y=radius in local coords (0, radius, 0),
               * so it's on the torus "surface." We do a small box, e.g. [0.08, 0.08, 0.02].
               */}
              <mesh position={[0, radius, 0]}>
                <boxGeometry args={[0.08, 0.08, 0.02]} />
                <meshStandardMaterial color={colors[i % colors.length]} />
              </mesh>
            </group>
          );
        }
        return <>{patches}</>;
      }
      
  
  /**
   * SimpleMachineDemo:
   *  Now cycles among:
   *   1) InteractiveSeesaw
   *   2) InteractiveInclinedPlane
   *   3) InteractiveWheelAxle
   */
function SimpleMachineDemo() {
  const machines = [
    {
      name: "Lever",
      description: "A lever helps lift things easily!",
      example: "A seesaw at the playground!",
      // Instead of the old Lever3D, we use our new InteractiveSeesaw
      model: <InteractiveSeesaw />,
    },
    {
      name: "Inclined Plane",
      description: "A ramp makes moving heavy things easier!",
      example: "A slide at the park!",
      model: <InteractiveInclinedPlane />,
    },
    {
      name: "Wheel & Axle",
      description: "Wheels help things move faster!",
      example: "A bicycle!",
      model: <InteractiveWheelAxle />,
    },
  ];

  // We store just a numeric index to avoid reference issues
  const [currentIndex, setCurrentIndex] = useState(0);

  // currentMachine is whichever object is at machines[currentIndex]
  const currentMachine = machines[currentIndex];

  const showNextMachine = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % machines.length);
  };

  return (
    <div className="g1eng-simple-machine-container">
      <h4>Meet a Simple Machine: {currentMachine.name}</h4>
      <p className="g1eng-machine-description">{currentMachine.description}</p>
      <p className="g1eng-machine-example">
        <strong>Example:</strong> {currentMachine.example}
      </p>

      {currentMachine.model && (
        <div style={{ marginTop: "20px" }}>
          {currentMachine.model}
        </div>
      )}

      <button className="g1eng-next-machine-btn" onClick={showNextMachine}>
        See Another Machine
      </button>
    </div>
  );
}

/** Shuffle array helper */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/** Build question bank => total 50 => final quiz picks 50 */
function buildQuestionBank(engineeringPool, machinesPool) {
  return [...engineeringPool, ...machinesPool];
}

/**
 * Grade1EngineeringLesson
 *  - “What is Engineering?” & “Simple Machines” topics
 *  - Quick Quiz & Final Quiz
 *  - 3D scenes for Lever, Inclined Plane, Wheel & Axle (with an interactive lever)
 */
export default function Grade1EngineeringLesson() {
  /**
   * (1) LESSON CONTENT
   */
  const lessonContent = {
    whatIsEngineering: {
      title: "What is Engineering?",
      description: "Engineers solve problems and build amazing things!",
      example: "Example: Engineers build robots, bridges, houses, and more!",
      steps: [
        "Engineers think, plan, and create!",
        "They use tools (like wrenches and blueprints) to build or fix things.",
        "They solve problems to help people—like making strong houses or fixing broken swings!",
        "Engineers love to figure out how things work, then improve them.",
        "They learn from mistakes by testing ideas and redesigning when plans fail.",
        "They often collaborate—working together to share ideas!",
        "Engineers can also design new inventions to make life easier for everyone.",
        "They use math and science to plan solutions and might draw designs or blueprints.",
        "If you enjoy building LEGO, you might also enjoy being an engineer!",
      ],
    },
    simpleMachines: {
      title: "Simple Machines",
      description: "Simple machines make work easier!",
      example: "A slide is an inclined plane!",
      steps: [
        "They are called 'simple' because they have few (or no) moving parts.",
        "A lever helps lift things by pivoting on a fulcrum (like a seesaw!).",
        "A wheel & axle helps things roll or move faster (like a bicycle wheel!).",
        "An inclined plane is a ramp that helps move heavy objects up/down (like a slide!).",
        "If you push a box up a ramp (inclined plane) instead of lifting it, you use less force.",
        "A lever’s pivot point is called a fulcrum (like the center of a seesaw).",
        "A handle and rod that help something turn is a wheel & axle (like a doorknob or a wagon).",
        "Another name for a ramp is an inclined plane (like a slide at the park).",
        "A seesaw is a lever, while a slide is an inclined plane!",
        // We render <SimpleMachineDemo> here
        <SimpleMachineDemo key="simple-machine-demo" />,
      ],
    },
  };
  

  /**
   * (2) QUIZ POOLS: 25 each => total 50
   */
  const engineeringPool = [
    {
      question: "What do engineers do?",
      correctAnswer: "They solve problems and build things",
      options: [
        "They only fix cars",
        "They watch TV all day",
        "They solve problems and build things",
      ],
    },
    {
      question: "Which is an example of something an engineer might build?",
      correctAnswer: "A bridge",
      options: ["A cake", "A painting", "A bridge"],
    },
    {
      question: "Engineers use tools to?",
      correctAnswer: "Build and fix things",
      options: [
        "Eat snacks",
        "Build and fix things",
        "Play video games only",
      ],
    },
    {
      question: "Which problem might an engineer solve?",
      correctAnswer: "How to make a strong house",
      options: [
        "How to pick an ice cream flavor",
        "How to do a magic trick",
        "How to make a strong house",
      ],
    },
    {
      question: "Engineers think, plan, and ___?",
      correctAnswer: "Create",
      options: ["Ignore", "Create", "Destroy"],
    },
    {
      question: "What is the main goal of engineering?",
      correctAnswer: "To help people by solving problems",
      options: [
        "To travel on vacation",
        "To help people by solving problems",
        "To dance all day",
      ],
    },
    {
      question: "Which tool might an engineer use?",
      correctAnswer: "A wrench",
      options: ["A paintbrush", "A wrench", "A spatula"],
    },
    {
      question: "Engineers work on robots, bridges, and?",
      correctAnswer: "Houses",
      options: ["Cupcakes", "Houses", "Rockets only"],
    },
    {
      question: "When engineers plan, they often draw?",
      correctAnswer: "Designs or blueprints",
      options: [
        "Silly cartoons",
        "Random doodles",
        "Designs or blueprints",
      ],
    },
    {
      question: "What do engineers love to do?",
      correctAnswer: "Figure out how things work",
      options: [
        "Write poems only",
        "Ignore all problems",
        "Figure out how things work",
      ],
    },
    {
      question: "Which is true about engineering?",
      correctAnswer: "It involves creativity and problem-solving",
      options: [
        "It’s only about memorizing facts",
        "It never uses math or science",
        "It involves creativity and problem-solving",
      ],
    },
    {
      question: "Engineers can help people by making?",
      correctAnswer: "New inventions",
      options: ["Ice cream shops", "New inventions", "Paintings only"],
    },
    {
      question: "If something is broken, an engineer might?",
      correctAnswer: "Fix or improve it",
      options: [
        "Sing a song to it",
        "Ignore it",
        "Fix or improve it",
      ],
    },
    {
      question: "What is an important step in engineering?",
      correctAnswer: "Planning before building",
      options: [
        "Building without thinking",
        "Planning before building",
        "Guessing randomly",
      ],
    },
    {
      question: "Engineers often learn from mistakes by?",
      correctAnswer: "Testing and redesigning",
      options: [
        "Throwing everything away",
        "Stopping forever",
        "Testing and redesigning",
      ],
    },
    {
      question: "Which subject might help an engineer?",
      correctAnswer: "Math and science",
      options: [
        "No subjects at all",
        "Math and science",
        "Only music",
      ],
    },
    {
      question: "Engineers might design solutions for?",
      correctAnswer: "Traffic problems, building safety, and more",
      options: [
        "Only coloring books",
        "Traffic problems, building safety, and more",
        "Just skipping chores",
      ],
    },
    {
      question: "An engineer sees a broken swing. They might?",
      correctAnswer: "Figure out how to fix or improve it",
      options: [
        "Sit and watch it",
        "Tell jokes about it",
        "Figure out how to fix or improve it",
      ],
    },
    {
      question: "Which word best describes engineering?",
      correctAnswer: "Creating solutions",
      options: [
        "Complaining a lot",
        "Creating solutions",
        "Painting pictures",
      ],
    },
    {
      question: "Engineers help build better?",
      correctAnswer: "Communities and devices",
      options: [
        "Dreams only",
        "Communities and devices",
        "Nonsense stories",
      ],
    },
    {
      question: "Why do engineers test their ideas?",
      correctAnswer: "To see if they work and to make improvements",
      options: [
        "Just for fun only, with no reason",
        "They never test anything",
        "To see if they work and to make improvements",
      ],
    },
    {
      question: "What can happen if an engineer’s plan fails?",
      correctAnswer: "They learn and try a new idea",
      options: [
        "They quit forever",
        "They learn and try a new idea",
        "They get angry and break things",
      ],
    },
    {
      question: "Engineers might also collaborate, which means?",
      correctAnswer: "Work together to share ideas",
      options: [
        "Argue all the time",
        "Work alone in a dark room",
        "Work together to share ideas",
      ],
    },
    {
      question: "If you like building LEGO, you might enjoy?",
      correctAnswer: "Becoming an engineer",
      options: [
        "Being a chef only",
        "Becoming an engineer",
        "Sleeping all day",
      ],
    },
    {
      question: "Which statement is true about engineering?",
      correctAnswer: "It can make life easier for everyone",
      options: [
        "It’s not useful to anyone",
        "It can make life easier for everyone",
        "It only applies to sports",
      ],
    },
  ];

  const machinesPool = [
    {
      question: "Which simple machine is like a seesaw?",
      correctAnswer: "Lever",
      options: ["Wheel", "Lever", "Inclined plane"],
    },
    {
      question: "A ramp is also called?",
      correctAnswer: "An inclined plane",
      options: ["A lever", "A car", "An inclined plane"],
    },
    {
      question: "Which helps move things faster?",
      correctAnswer: "Wheel & axle",
      options: ["Inclined plane", "Book", "Wheel & axle"],
    },
    {
      question: "A slide at the playground is an example of?",
      correctAnswer: "An inclined plane",
      options: [
        "A lever",
        "An inclined plane",
        "A wheel & axle",
      ],
    },
    {
      question: "A seesaw helps lift people up. It’s a?",
      correctAnswer: "Lever",
      options: ["Wheel", "Lever", "Ramp"],
    },
    {
      question: "Why do we call them simple machines?",
      correctAnswer: "They have few or no moving parts",
      options: [
        "They’re not helpful",
        "They have few or no moving parts",
        "They are made of candy",
      ],
    },
    {
      question: "Which simple machine might help lift a heavy rock?",
      correctAnswer: "A lever",
      options: ["A pillow", "A TV remote", "A lever"],
    },
    {
      question: "What does a wheel do?",
      correctAnswer: "Helps things roll or move faster",
      options: [
        "Stops things from moving",
        "Helps things roll or move faster",
        "Makes water appear",
      ],
    },
    {
      question: "An inclined plane helps by?",
      correctAnswer: "Making it easier to move heavy objects up/down",
      options: [
        "Flying people around",
        "Cooking dinner",
        "Making it easier to move heavy objects up/down",
      ],
    },
    {
      question: "Which is an example of a wheel & axle?",
      correctAnswer: "A bicycle wheel",
      options: ["A rock", "A spoon", "A bicycle wheel"],
    },
    {
      question: "If you push a box up a ramp instead of lifting it, you use a?",
      correctAnswer: "Inclined plane",
      options: [
        "Lever",
        "Wheel & axle",
        "Inclined plane",
      ],
    },
    {
      question: "A lever’s pivot point is called a?",
      correctAnswer: "Fulcrum",
      options: ["Edge", "Fulcrum", "Circle"],
    },
    {
      question: "Which simple machine might be found on a wagon?",
      correctAnswer: "Wheel & axle",
      options: [
        "Inclined plane",
        "Fulcrum only",
        "Wheel & axle",
      ],
    },
    {
      question: "Pulling down on one side to lift the other describes a?",
      correctAnswer: "Lever",
      options: ["Screw", "Lever", "Inclined plane"],
    },
    {
      question: "How do simple machines help us?",
      correctAnswer: "They make work easier",
      options: [
        "They make things harder",
        "They break everything",
        "They make work easier",
      ],
    },
    {
      question: "A handle and a rod that help something turn is a?",
      correctAnswer: "Wheel & axle",
      options: [
        "Inclined plane",
        "Wheel & axle",
        "Hammer",
      ],
    },
    {
      question: "Which might be a lever at a playground?",
      correctAnswer: "A seesaw",
      options: [
        "A climbing wall",
        "A swing set",
        "A seesaw",
      ],
    },
    {
      question: "A ramp for loading furniture into a truck is an example of?",
      correctAnswer: "Inclined plane",
      options: [
        "Lever",
        "Wheel & axle",
        "Inclined plane",
      ],
    },
    {
      question: "Which machine helps rolling suitcases move smoothly?",
      correctAnswer: "Wheels",
      options: [
        "Levers",
        "Wheels",
        "Inclined planes",
      ],
    },
    {
      question: "If you push a shopping cart, you use a?",
      correctAnswer: "Wheel & axle",
      options: [
        "Lever system",
        "Wheel & axle",
        "Inclined plane",
      ],
    },
    {
      question: "What is a simple machine that changes direction of force to lift?",
      correctAnswer: "A lever",
      options: [
        "A lever",
        "A banana",
        "A clock",
      ],
    },
    {
      question: "How do simple machines make work easier?",
      correctAnswer: "They reduce effort by spreading force",
      options: [
        "They do nothing useful",
        "They reduce effort by spreading force",
        "They force you to do more work",
      ],
    },
    {
      question: "Which of these is NOT a simple machine?",
      correctAnswer: "A computer chip",
      options: [
        "A see-saw (lever)",
        "A ramp (inclined plane)",
        "A computer chip",
      ],
    },
    {
      question: "Another name for a ramp is?",
      correctAnswer: "Inclined plane",
      options: [
        "Lever",
        "Circle plane",
        "Inclined plane",
      ],
    },
    {
      question: "If a door handle helps rotate the latch, it’s acting like a?",
      correctAnswer: "Wheel & axle",
      options: [
        "Inclined plane",
        "Wheel & axle",
        "Lever pivot",
      ],
    },
  ]; 

  // (3) States for quick quiz and final quiz
  const [selectedTopic, setSelectedTopic] = useState(null);

  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [fullQuizQuestions, setFullQuizQuestions] = useState([]);
  const [fullQuizActive, setFullQuizActive] = useState(false);
  const [fullQuizIndex, setFullQuizIndex] = useState(0);
  const [fullQuizScore, setFullQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes

  // (4) Timer effect for final quiz
  useEffect(() => {
    if (fullQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFullQuizActive(false);
    }
  }, [fullQuizActive, timeLeft]);

  // (5) Quick Quiz
  const generateQuiz = (topic) => {
    let questionItem;
    if (topic === "whatIsEngineering") {
      questionItem = engineeringPool[Math.floor(Math.random() * engineeringPool.length)];
    } else {
      questionItem = machinesPool[Math.floor(Math.random() * machinesPool.length)];
    }
    const newQuiz = {
      question: questionItem.question,
      correctAnswer: questionItem.correctAnswer,
      options: shuffleArray([...questionItem.options]),
    };
    setQuiz(newQuiz);
    setSelectedAnswer(null);
    setFeedback(null);
  };

  const handleAnswerClick = (answer) => {
    setSelectedAnswer(answer);
    if (quiz && answer === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    setTimeout(() => {
      if (selectedTopic) generateQuiz(selectedTopic);
    }, 1500);
  };

  // (6) Final Quiz => pick all 50
  const startFinalQuiz = () => {
    const bigPool = buildQuestionBank(engineeringPool, machinesPool);
    const finalSet = shuffleArray(bigPool).slice(0, 50);
    setFullQuizQuestions(finalSet);
    setFullQuizActive(true);
    setFullQuizIndex(0);
    setFullQuizScore(0);
    setTimeLeft(300);
  };

  const handleFinalQuizAnswer = (choice) => {
    if (fullQuizQuestions[fullQuizIndex] && choice === fullQuizQuestions[fullQuizIndex].correctAnswer) {
      setFullQuizScore((prev) => prev + 1);
    }
    if (fullQuizIndex < fullQuizQuestions.length - 1) {
      setFullQuizIndex((prev) => prev + 1);
    } else {
      setFullQuizActive(false);
    }
  };

  return (
    <div className="g1eng-page">
      <h1 className="g1eng-title">Grade 1 Engineering Lessons</h1>
      <p className="g1eng-description">Click a topic to learn more:</p>

      {/* Topic Buttons */}
      <div className="g1eng-topics">
        <button
          className="g1eng-topic-card"
          onClick={() => {
            setSelectedTopic("whatIsEngineering");
            generateQuiz("whatIsEngineering");
          }}
        >
          What is Engineering?
        </button>
        <button
          className="g1eng-topic-card"
          onClick={() => {
            setSelectedTopic("simpleMachines");
            generateQuiz("simpleMachines");
          }}
        >
          Simple Machines
        </button>
      </div>

      {selectedTopic && (
        <div className="g1eng-lesson-quiz-container">
          {/* Lesson Content (Left) */}
          <div className="g1eng-lesson-content">
            <h2 className="g1eng-lesson-title">{lessonContent[selectedTopic].title}</h2>
            <p className="g1eng-lesson-description">{lessonContent[selectedTopic].description}</p>
            <p className="g1eng-lesson-example">
              <strong>{lessonContent[selectedTopic].example}</strong>
            </p>
            <ul className="g1eng-lesson-steps">
              {lessonContent[selectedTopic].steps.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ul>
          </div>

          {/* Right: Quick Quiz + Final Quiz side by side */}
          <div className="g1eng-quiz-double-container">
            {/* Quick Quiz */}
            <div className="g1eng-quick-quiz-container">
              <h2 className="g1eng-quiz-title">Quick Quiz!</h2>
              {quiz && (
                <>
                  <p className="g1eng-quiz-question">{quiz.question}</p>
                  <div className="g1eng-quiz-options">
                    {quiz.options.map((opt, i) => (
                      <button
                        key={i}
                        className={`g1eng-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                        onClick={() => handleAnswerClick(opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {feedback && <p className="g1eng-quiz-feedback">{feedback}</p>}
                </>
              )}
            </div>

            {/* Final Quiz */}
            <div className="g1eng-final-quiz-container">
              <h2 className="g1eng-quiz-title">Final Quiz</h2>
              {!fullQuizActive && (
                <button className="g1eng-final-quiz-button" onClick={startFinalQuiz}>
                  Start Final Quiz (50 Questions)
                </button>
              )}
              {fullQuizActive && (
                <div className="g1eng-final-quiz-content">
                  <p>
                    Time Left: {Math.floor(timeLeft / 60)}:
                    {String(timeLeft % 60).padStart(2, "0")}
                  </p>
                  <p>
                    Question {fullQuizIndex + 1} of {fullQuizQuestions.length}
                  </p>
                  <p>{fullQuizQuestions[fullQuizIndex].question}</p>
                  <div className="g1eng-quiz-options">
                    {fullQuizQuestions[fullQuizIndex].options.map((choice, index) => (
                      <button key={index} onClick={() => handleFinalQuizAnswer(choice)}>
                        {choice}
                      </button>
                    ))}
                  </div>
                  <p>Score: {fullQuizScore}</p>
                  {fullQuizIndex === fullQuizQuestions.length - 1 && (
                    <p>
                      Final Score: {fullQuizScore}/{fullQuizQuestions.length}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Link to="/engineering" className="g1eng-back-button">
        ← Back to Engineering Page
      </Link>
    </div>
  );
}
